import { useState } from "react";
import type { Id } from "convex/_generated/dataModel";
import { MapPin, Clock, Star, Share2, ExternalLink, Repeat, Pencil, Check, X, Archive } from "lucide-react";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { formatTimeRange } from "@/lib/dateUtils";
import { formatPrice, priceUnitSuffix } from "@/lib/price";
import { formatDistanceMiles } from "@/lib/distance";
import { cn } from "@/lib/utils";
import { EventStyleBadges } from "./DanceStyleBadge";
import { DanceStartLine } from "./DanceStartLine";
import { useFavorites } from "@/hooks/useFavorites";
import { EVENT_TAG_MAP } from "@/lib/constants";
import { useAdminMode } from "@/contexts/AdminModeContext";
import { EventEditModal } from "../forms/EventEditModal";
import { RecurringActionDialog, type RecurringAction } from "./RecurringActionDialog";

export interface Event {
  _id: Id<"events"> | string;
  title: string;
  description?: string;
  date: string;
  startTime: string;
  endTime?: string;
  /** Start of the class some socials open with, if the organizer gave one. */
  lessonStartTime?: string;
  /** End of that class — and so, when open dancing starts. */
  lessonEndTime?: string;
  venueName: string;
  venueAddress?: string;
  venueLatitude?: number;
  venueLongitude?: number;
  /**
   * Miles from the zip the reader searched, set only while a location filter
   * is active. Absent means "not measured", which is not the same as zero.
   */
  distanceMiles?: number;
  danceStyles: string[];
  mainStyles?: string[];
  eventUrl?: string;
  venueId?: Id<"venues">;
  sourceLinks?: { label: string; url: string }[];
  isRecurring?: boolean;
  patternId?: Id<"recurringPatterns">;
  priceMin?: number;
  priceMax?: number;
  priceUnit?: string;
  tags?: string[];
  status?: string;
  flyerUrl?: string;
  submitterLogoUrl?: string;
}

/**
 * Series context for a collapsed list entry: this card stands for a whole
 * series, so it says how often it runs and how many dates are coming rather
 * than pretending to be a single night.
 */
export type SeriesInfo = {
  cadenceLabel: string;
  /**
   * Absent when the card stands for one specific night rather than the whole
   * series — in all-dates mode the reader is already looking at every date,
   * so counting them again says nothing.
   */
  countNext3Months?: number;
  /**
   * False when the series has only one date left. A pattern row alone does not
   * make something recurring — a series down to its last occurrence would
   * otherwise render a repeat icon labelled "One-off".
   */
  isRecurring: boolean;
};

interface EventCardProps {
  event: Event;
  onClick: () => void;
  seriesInfo?: SeriesInfo;
}

export function EventCard({ event, onClick, seriesInfo }: EventCardProps) {
  const { isEventFavorited, toggleEventFavorite, canFavorite } = useFavorites();
  const isFavorited = isEventFavorited(event);
  const { isAdminMode, showAdminControls } = useAdminMode();
  const [editOpen, setEditOpen] = useState(false);
  const [editScope, setEditScope] = useState<"single" | "all">("single");
  const [copied, setCopied] = useState(false);
  const [recurringDialog, setRecurringDialog] = useState<RecurringAction | null>(null);

  const approveEvent = useMutation(api.moderation.approveEvent);
  const rejectEvent = useMutation(api.moderation.rejectEvent);
  const archiveEvent = useMutation(api.moderation.archiveEvent);
  const moderateSeries = useMutation(api.moderation.moderateSeriesEvents);

  // Event is recurring if it has a patternId (real DB field, no synthetic IDs)
  const isRecurringEvent = !!event.patternId;

  const priceLabel = formatPrice(event.priceMin, event.priceMax);
  // Per-person is the assumption, so the card only spells the unit out when
  // it is something else — that is the case a dancer needs to notice.
  const priceUnit = priceUnitSuffix(event.priceUnit, event.priceMin);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Following needs an account; send signed-out visitors to sign in rather
    // than swallowing the click.
    if (!canFavorite) {
      window.location.href = "/login";
      return;
    }
    void toggleEventFavorite(event._id, event.title);
  };

  const handleShareClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/events?event=${event._id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: event.title, text: `${event.title} at ${event.venueName}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // share cancelled or clipboard unavailable — fail silently
    }
  };

  const handleExternalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // For recurring events, show scope dialog first; for one-off, execute directly
  const handleAdminAction = (e: React.MouseEvent, action: RecurringAction) => {
    e.stopPropagation();
    if (isRecurringEvent) {
      setRecurringDialog(action);
    } else {
      executeAction(action, event._id as Id<"events">);
    }
  };

  const executeAction = async (action: RecurringAction, eventId: Id<"events">) => {
    if (action === "edit") {
      setEditOpen(true);
    } else if (action === "approve") {
      await approveEvent({ eventId });
    } else if (action === "reject") {
      await rejectEvent({ eventId });
    } else if (action === "archive") {
      await archiveEvent({ eventId });
    }
  };

  const statusColor =
    event.status === "approved" ? "bg-green-500/15 text-green-400" :
    event.status === "pending"  ? "bg-yellow-500/15 text-yellow-400" :
    event.status === "hidden"   ? "bg-red-500/15 text-red-400" : null;

  return (
    <>
      <article
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
        className={cn(
          "bg-card rounded-2xl overflow-hidden transition-all duration-300 ease-out",
          "border hover:border-border/60",
          showAdminControls && event.status === "pending" ? "border-yellow-500/50" :
          showAdminControls && event.status === "hidden"  ? "border-red-500/50" :
          "border-border",
          "hover:shadow-xl hover:shadow-black/25",
          "cursor-pointer group outline-none focus-visible:ring-2 focus-visible:ring-primary"
        )}
      >
        {/* Header: Dance styles + Time.
            The whole row wraps: when an event carries enough styles that the
            badges and the time cannot share a line, the time drops to the next
            one. Previously the time block refused to shrink and the badges were
            squeezed until they spilled out over it. */}
        <div className="px-5 pt-5 pb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex flex-wrap gap-1.5 items-center min-w-0">
            {(seriesInfo
              ? seriesInfo.isRecurring
              : isRecurringEvent || event.isRecurring) && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-full whitespace-nowrap">
                <Repeat className="w-3 h-3 shrink-0" />
                {seriesInfo && (
                  <span className="whitespace-nowrap">
                    {seriesInfo.cadenceLabel}
                  </span>
                )}
              </span>
            )}
            {showAdminControls && statusColor && (
              <span className={cn("text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide whitespace-nowrap", statusColor)}>
                {event.status}
              </span>
            )}
            <EventStyleBadges danceStyles={event.danceStyles} mainStyles={event.mainStyles} />
          </div>
          <div className="flex items-center gap-2 text-muted-foreground shrink-0 ml-auto">
            {priceLabel && (
              <span
                className={cn(
                  "text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap",
                  priceLabel === "Free"
                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                    : "bg-secondary text-foreground/80"
                )}
              >
                {priceLabel}
                {priceUnit && (
                  <span className="font-normal opacity-80"> {priceUnit}</span>
                )}
              </span>
            )}
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              <span className="text-sm font-medium">
                {formatTimeRange(event.startTime, event.endTime)}
              </span>
            </div>
          </div>
        </div>

        {/* When the floor opens — its own row, so it cannot crowd the badges
            out of the header. Renders nothing when there is no lesson. */}
        <DanceStartLine event={event} variant="full" className="px-5 -mt-1 pb-2" />

        {/* Main content */}
        <div className="px-5 pb-4">
          <h3 className="font-semibold text-foreground text-xl leading-tight mb-1 group-hover:text-primary transition-colors duration-200">
            {event.title}
          </h3>

          {event.tags && event.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {event.tags.map((slug) => {
                const tag = EVENT_TAG_MAP[slug];
                if (!tag) return null;
                return (
                  <span
                    key={slug}
                    className="text-[11px] font-medium text-foreground/75 bg-secondary px-2 py-0.5 rounded-full border border-border/50 whitespace-nowrap"
                  >
                    {tag.label}
                  </span>
                );
              })}
            </div>
          )}

          <div className="flex items-center gap-2 text-muted-foreground mb-3 min-w-0">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm truncate">{event.venueName}</span>
            {event.venueAddress && (
              <>
                <span className="text-border flex-shrink-0">·</span>
                <span className="text-sm truncate">{event.venueAddress}</span>
              </>
            )}
            {event.distanceMiles !== undefined && (
              <span className="ml-auto flex-shrink-0 text-xs font-medium text-foreground/80 bg-secondary px-2 py-0.5 rounded-full">
                {formatDistanceMiles(event.distanceMiles)}
              </span>
            )}
          </div>

          {event.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {event.description}
            </p>
          )}
        </div>

        {/* Action bar */}
        <div className="px-5 py-3 bg-card-footer flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={handleFavoriteClick}
              className={cn(
                "p-2 rounded-lg transition-all duration-200",
                "hover:bg-secondary",
                isFavorited ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
              aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
            >
              <Star className="w-5 h-5" fill={isFavorited ? "currentColor" : "none"} />
            </button>
            <button
              onClick={handleShareClick}
              className={cn(
                "p-2 rounded-lg transition-all duration-200",
                copied
                  ? "text-green-500"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              )}
              aria-label={copied ? "Link copied" : "Share event"}
            >
              {copied ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />}
            </button>
            {event.eventUrl && (
              <a
                href={event.eventUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleExternalClick}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all duration-200"
                aria-label="View event page"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            )}
          </div>

          {showAdminControls ? (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => handleAdminAction(e, "edit")}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                title="Edit event"
              >
                <Pencil className="w-4 h-4" />
              </button>
              {event.status !== "approved" && (
                <button
                  onClick={(e) => handleAdminAction(e, "approve")}
                  className="p-2 rounded-lg text-green-500 hover:bg-green-500/10 transition-colors"
                  title="Approve event"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}
              {event.status !== "hidden" && (
                <button
                  onClick={(e) => handleAdminAction(e, "reject")}
                  className="p-2 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors"
                  title="Reject / hide event"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              {event.status !== "archived" && (
                <button
                  onClick={(e) => handleAdminAction(e, "archive")}
                  className="p-2 rounded-lg text-orange-500 hover:bg-orange-500/10 transition-colors"
                  title="Archive event (30-day restore window)"
                >
                  <Archive className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : event.submitterLogoUrl ? (
            <img
              src={event.submitterLogoUrl}
              alt="Studio logo"
              className="h-7 w-7 rounded-full object-cover border border-border/50"
            />
          ) : seriesInfo?.countNext3Months && seriesInfo.countNext3Months > 1 ? (
            // Says what the single date on this card actually stands for, so a
            // collapsed series does not read as a one-night event.
            <span className="text-xs text-muted-foreground font-medium">
              {seriesInfo.countNext3Months} dates in next 3 months
            </span>
          ) : (
            <span className="text-xs text-muted-foreground font-medium">Tap for details</span>
          )}
        </div>
      </article>

      {recurringDialog && event.patternId && (
        <RecurringActionDialog
          action={recurringDialog}
          patternId={event.patternId}
          onClose={() => setRecurringDialog(null)}
          onThisEvent={() => {
            setRecurringDialog(null);
            if (recurringDialog === "edit") {
              setEditScope("single");
              setEditOpen(true);
            } else {
              executeAction(recurringDialog, event._id as Id<"events">);
            }
          }}
          onAllEvents={async () => {
            const action = recurringDialog;
            setRecurringDialog(null);
            if (action === "edit") {
              setEditScope("all");
              setEditOpen(true);
            } else if (action && event.patternId) {
              await moderateSeries({ patternId: event.patternId, action });
            }
          }}
        />
      )}

      {editOpen && (
        <EventEditModal
          event={event as any}
          onClose={() => setEditOpen(false)}
          onSaved={() => setEditOpen(false)}
          editScope={editScope}
        />
      )}
    </>
  );
}
